// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Bandeau de suivi des analyses (chantier ⑮, décision de Marc du 30/09/2026).
//
// L'analyse en cours n'est plus gardée que dans l'ONGLET qui l'a lancée
// (`sessionStorage`) : fermer l'onglet l'oublie côté navigateur. Mais l'analyse
// elle-même tourne sur le SERVEUR et continue. Ce bandeau, posé dans la coque
// commune (AppLayout), le dit à la réouverture :
//   - en cours : fichier + pourcentage, rafraîchi toutes les 10 s ;
//   - terminée et pas encore vue : où retrouver ses conclusions.
// Il ne parle PAS de l'analyse que cet onglet suit déjà (NORMA s'en charge).
//
// Il porte aussi l'AVERTISSEMENT À LA FERMETURE : fermer ou recharger l'onglet
// pendant qu'une analyse de cet onglet tourne demande confirmation. ⚠️ Les
// navigateurs n'affichent plus de texte personnalisé dans cette fenêtre : c'est
// leur message standard (« Quitter le site ? »). L'explication est ici, dans
// le bandeau que l'on trouve en revenant.
//
// La source est `/api/suivi-analyses` ; « vue » est posé côté serveur
// (`resultat_vu_le`, révision 0029) — l'information suit le COMPTE d'un poste
// à l'autre.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { CheckCircle2, Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { getSuiviAnalyses, marquerResultatVu } from '../services/api';

const INTERVALLE_MS = 10000;

// L'analyse que CET onglet suit (Dashboard.js), s'il y en a une.
const analyseDeLOnglet = () => {
  try { return JSON.parse(sessionStorage.getItem('normascan_current_analysis') || 'null'); }
  catch (_) { return null; }
};

const SuiviAnalysesBanner = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [, setLocation] = useLocation();
  const [suivi, setSuivi] = useState({ en_cours: [], terminees: [] });
  const [masquees, setMasquees] = useState(() => new Set());   // en cours masquées (mémoire)
  // Ce que le serveur dit tourner, pour les écouteurs posés une seule fois.
  const enCoursServeurRef = useRef(new Set());

  const charger = useCallback(() => (
    getSuiviAnalyses()
      .then((d) => {
        setSuivi({ en_cours: d.en_cours || [], terminees: d.terminees || [] });
        enCoursServeurRef.current = new Set((d.en_cours || []).map((a) => a.id));
      })
      .catch(() => { /* le bandeau se tait plutôt que d'afficher une erreur */ })
  ), []);

  // Au chargement, puis toutes les 10 s TANT QUE quelque chose tourne (ici ou
  // ailleurs) — aucun appel réseau quand rien n'est en cours.
  useEffect(() => {
    if (!user?.id) return undefined;
    charger();
    const minuterie = setInterval(() => {
      if (enCoursServeurRef.current.size > 0 || analyseDeLOnglet()?.status === 'PROCESSING') charger();
    }, INTERVALLE_MS);
    return () => clearInterval(minuterie);
  }, [user?.id, charger]);

  // Avertissement à la fermeture : seulement si l'analyse de CET onglet tourne
  // encore d'après le serveur (pas une analyse finie restée en mémoire).
  useEffect(() => {
    const avantFermeture = (e) => {
      const onglet = analyseDeLOnglet();
      if (onglet?.analysis_id && enCoursServeurRef.current.has(onglet.analysis_id)) {
        e.preventDefault();
        e.returnValue = '';   // exigé par Chrome pour afficher la confirmation
      }
    };
    window.addEventListener('beforeunload', avantFermeture);
    return () => window.removeEventListener('beforeunload', avantFermeture);
  }, []);

  const idOnglet = analyseDeLOnglet()?.analysis_id;
  const enCours = suivi.en_cours.filter((a) => a.id !== idOnglet && !masquees.has(a.id));
  const terminees = suivi.terminees.filter((a) => a.id !== idOnglet);
  if (enCours.length === 0 && terminees.length === 0) return null;

  // Masquer une terminée = elle est vue (serveur) ; une en cours, pour cette
  // page seulement — sa fin, elle, sera annoncée.
  const masquer = (analyse, terminee) => {
    if (terminee) {
      marquerResultatVu(analyse.id);
      setSuivi((s) => ({ ...s, terminees: s.terminees.filter((a) => a.id !== analyse.id) }));
    } else {
      setMasquees((m) => new Set(m).add(analyse.id));
    }
  };

  const voirMesAnalyses = () => {
    terminees.forEach((a) => marquerResultatVu(a.id));
    setSuivi((s) => ({ ...s, terminees: [] }));
    setLocation('/analyses');
  };

  const ligne = (analyse, terminee) => {
    const texte = terminee
      ? t('suivi_terminee')
      : (analyse.status === 'PENDING' ? t('suivi_en_file') : t('suivi_en_cours'));
    const couleur = terminee ? '#15803d' : '#1d4ed8';
    return (
      <div key={analyse.id} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 20px', background: terminee ? 'rgba(21,128,61,0.08)' : 'rgba(29,78,216,0.08)',
        borderBottom: `1px solid ${couleur}`, color: couleur, fontSize: 13, lineHeight: 1.4,
      }}>
        {terminee
          ? <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
          : <Loader2 size={16} className="animate-spin" style={{ flexShrink: 0 }} />}
        <span style={{ flex: 1 }}>
          {texte.replace('{fichier}', analyse.filename || '').replace('{pct}', String(analyse.progress || 0))}
        </span>
        <button type="button" onClick={voirMesAnalyses} style={{
          background: 'none', border: `1px solid ${couleur}`, color: couleur, borderRadius: 6,
          padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        }}>
          {t('suivi_voir')}
        </button>
        <button type="button" onClick={() => masquer(analyse, terminee)}
                aria-label={t('suivi_fermer')} title={t('suivi_fermer')}
                style={{ background: 'none', border: 'none', color: couleur, cursor: 'pointer', padding: 2 }}>
          <X size={16} />
        </button>
      </div>
    );
  };

  return (
    <div role="status">
      {enCours.map((a) => ligne(a, false))}
      {terminees.map((a) => ligne(a, true))}
    </div>
  );
};

export default SuiviAnalysesBanner;
